import { Card, CardContent } from "@/components/ui/card";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useDimensionByQuizId } from "@/hooks/dimension-by-quiz-id.hook";
import { getDimensionById } from "@/lib/dimensions";
import { DimensionId } from "@/types/questionnaries";
import React, { CSSProperties, FC, HTMLAttributes } from "react";
import DimensionHeaderSkeleton from "./dimension-header-skeleton";

type DimensionHeaderProps = HTMLAttributes<HTMLDivElement> & {
  dimensionId: DimensionId;
  quizId: string;
};

const DimensionHeader: FC<DimensionHeaderProps> = ({
  quizId,
  dimensionId,
  ...props
}) => {
  const dimension = getDimensionById(dimensionId);

  if (!dimension) {
    throw new Error("Dimension not found");
  }

  const { data: userProfile } = useCurrentUserProfile();

  const { data: dimensionSummary, isLoading: isDimensionSummaryLoading } =
    useDimensionByQuizId({
      companyId: userProfile?.companyId || "",
      applicationQuizId: quizId || "",
      dimension: dimensionId,
    });

  if (isDimensionSummaryLoading) {
    return <DimensionHeaderSkeleton />;
  }

  if (!dimensionSummary) {
    return null;
  }

  return (
    <Card className={props.className}>
      <CardContent className="flex flex-row items-center gap-4 p-6">
        <div className="grow">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: dimension?.color }}
            >
              {dimension.icon && React.createElement(dimension.icon)}
            </div>
            <p className="text-3xl font-semibold">{dimension.label}</p>
          </div>

          <p className="mt-3">{dimensionSummary?.description}</p>
        </div>

        {dimensionSummary.result && (
          <div
            style={{ "--dimension-color": dimension?.color } as CSSProperties}
          >
            <p className="mb-2 text-center text-sm">Puntaje de la dimensión:</p>
            <div
              className="w-[180px] rounded-xl border-2 py-3 text-center"
              style={{
                borderColor: "var(--dimension-color)",
                backgroundColor:
                  "color-mix(in srgb, var(--dimension-color), white 90%)",
              }}
            >
              <span className="text-lg font-bold text-black">
                {dimensionSummary?.result?.score}
              </span>
              <span
                className="text-lg font-bold"
                style={{
                  color:
                    "color-mix(in srgb, var(--dimension-color), black 30%)",
                }}
              >
                /{dimensionSummary?.result?.scoreProportion}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DimensionHeader;
